import type { ReactElement } from 'react';
import {
  TELEMETRY_SEVERITY,
  type StoredTelemetryEvent,
  type TelemetryEvent,
  type TelemetrySeverity,
} from '@proctoring/shared';

/**
 * Utility classes per severity.
 *
 * Deliberately mirrors `SEVERITY_CLASSES` in @proctoring/session-ui rather than importing
 * it: that package pulls LiveKit and the whole session runtime into the bundle, which the
 * backoffice never renders. The palette is the same (see styles.css), so the two feeds
 * still read identically.
 */
const SEVERITY_CLASSES: Readonly<Record<TelemetrySeverity, string>> = {
  info: 'border-l-severity-info text-severity-info',
  warning: 'border-l-severity-warning text-severity-warning',
  critical: 'border-l-severity-critical text-severity-critical',
};

/**
 * A one-line human summary per event type.
 *
 * The exhaustive switch is load-bearing: adding a variant to the shared discriminated union
 * without describing it here becomes a compile error rather than a blank row in what is
 * meant to be evidence.
 */
function describe(event: TelemetryEvent): { title: string; detail: string } {
  switch (event.type) {
    case 'focus_lost':
      return {
        title: 'Focus lost',
        detail: `Focus moved to ${event.processName}${event.windowTitle ? ` — “${event.windowTitle}”` : ''}.`,
      };
    case 'focus_regained':
      return {
        title: 'Focus regained',
        detail: `Away for ${Math.round(event.awayMs / 1000)}s.`,
      };
    case 'overlay_window_detected':
      return {
        title: 'Overlay window detected',
        detail: `${event.processName}${
          event.captureTransparent
            ? ' — present in window enumeration but absent from capture (capture-transparent).'
            : '.'
        }`,
      };
    case 'process_launched':
      return {
        title: 'Process launched',
        detail: `${event.processName} (pid ${event.pid}).`,
      };
    case 'display_connected':
      return {
        title: 'Display connected',
        detail: `${event.displayCount} displays now connected.`,
      };
    case 'display_disconnected':
      return {
        title: 'Display disconnected',
        detail: `${event.displayCount} displays now connected.`,
      };
    case 'vm_or_remote_desktop_detected':
      return {
        title: 'VM or remote desktop detected',
        detail: `Indicator: ${event.indicator}.`,
      };
    case 'session_terminated_by_candidate':
      return {
        title: 'Session terminated by candidate',
        detail: 'The candidate closed the app, ending the session.',
      };
    case 'session_disconnected_unexpectedly':
      return {
        title: 'Connection lost unexpectedly',
        detail: 'The candidate app disconnected without leaving the session.',
      };
    case 'permission_revoked_midsession':
      return {
        title: 'Permission revoked mid-session',
        detail: `${event.permission.replace(/_/g, ' ')} access was withdrawn.`,
      };
    case 'network_dropped':
      return { title: 'Network dropped', detail: 'The candidate lost connectivity.' };
    case 'network_rejoined':
      return {
        title: 'Network rejoined',
        detail: `Reconnected after ${Math.round(event.downtimeMs / 1000)}s.`,
      };
    /*
     * Browsing rows (§3). The policy is open — any site is allowed because every URL is
     * here and on the screen share — so these read as a record of where the candidate went,
     * not as findings.
     */
    case 'browser_navigated':
      return {
        title: 'Page visited',
        detail:
          event.source === 'search' && event.query !== undefined
            ? `Searched for “${event.query}” — ${event.url}`
            : `${event.source === 'interviewer' ? 'Pushed by an interviewer: ' : ''}${event.url}`,
      };
    case 'browser_tab_opened':
      return {
        title: 'Tab opened',
        detail: `${event.openedBy === 'page' ? 'A page opened a tab' : 'The candidate opened a tab'} — ${event.tabCount} open.`,
      };
    case 'browser_tab_closed':
      return {
        title: 'Tab closed',
        detail: `${event.url || 'A blank tab'} — ${event.tabCount} still open.`,
      };
    case 'browser_tab_switched':
      return { title: 'Tab switched', detail: `Switched to ${event.url || 'a blank tab'}.` };
    /*
     * What the proctoring side handed the candidate, quoted verbatim. The one row here that
     * records an interviewer's action rather than an observation of the candidate — and the
     * reason a reviewer can tell "the candidate pasted a credential" from "the candidate was
     * given a credential".
     */
    case 'interviewer_message_sent':
      return {
        title: 'Message sent to candidate',
        detail: `${event.sentByName}: “${event.body.trim()}”`,
      };
    /*
     * The two ends of a visibility gap. Sharing is consent-based and optional, so these read
     * as bookends on a period the screen was not visible — the pair is what makes the gap
     * measurable in an export — not as findings against the candidate.
     */
    case 'screen_share_started':
      return {
        title: 'Screen share started',
        detail: 'The candidate started sharing their screen.',
      };
    case 'screen_share_stopped':
      return {
        title: 'Screen share stopped',
        detail:
          event.reason === 'track_ended'
            ? 'The capture ended on the candidate’s machine (permission or display change).'
            : 'The candidate stopped sharing.',
      };
  }
}

function formatTimestamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export interface TelemetryLogViewProps {
  events: StoredTelemetryEvent[];
  emptyMessage?: string;
  className?: string;
}

/** The admin's read-only evidence log for a finished interview. */
export function TelemetryLogView({
  events,
  emptyMessage = 'No events were recorded for this interview.',
  className,
}: TelemetryLogViewProps): ReactElement {
  // Newest first: the last thing that happened is what an admin opens the log to see.
  const ordered = [...events].sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));

  if (ordered.length === 0) {
    return (
      <p className={`panel px-5 py-6 text-sm text-session-muted ${className ?? ''}`}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <ol
      aria-label="Telemetry log"
      className={`panel divide-y divide-session-border overflow-hidden ${className ?? ''}`}
    >
      {ordered.map((stored) => {
        const severity = TELEMETRY_SEVERITY[stored.event.type];
        const description = describe(stored.event);
        return (
          <li
            key={stored.id}
            data-testid="telemetry-row"
            data-severity={severity}
            data-event-type={stored.event.type}
            className={`border-l-2 px-5 py-3 ${SEVERITY_CLASSES[severity]}`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium">{description.title}</span>
              <time
                dateTime={stored.event.timestamp}
                className="tabular-nums-tight shrink-0 text-xs text-session-dim"
              >
                {formatTimestamp(stored.event.timestamp)}
              </time>
            </div>
            <p className="mt-0.5 text-sm text-session-muted">{description.detail}</p>
          </li>
        );
      })}
    </ol>
  );
}
